import {
  Divider,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  chakra,
  forwardRef,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { ColorfulBar } from './ColorfulBar';
import { CurrencyLabel } from './Currency';

const minWidth = '120px';

export interface InvoiceVsCommitmentBarProps {
  commitment: any | undefined;
  invoice: any | undefined;
  pendingInvoice: any | undefined;
}

export const InvoicedVsCommitmentBar = chakra(
  (props: InvoiceVsCommitmentBarProps) => {
    const { commitment = 0, invoice = 0, pendingInvoice = 0, ...other } = props;

    const commitmentTotal = useMemo(() => parseFloat(commitment), [commitment]);
    const remaining = useMemo(() => {
      return Math.max(
        0,
        commitmentTotal - (parseFloat(invoice) + parseFloat(pendingInvoice))
      );
    }, [commitmentTotal, invoice, pendingInvoice]);

    const Notes = () => (
      <Stack p={{ base: 1, md: 2 }}>
        <SimpleGrid columns={2} gap={{ base: 1, md: 2 }}>
          <Text as="b">Invoiced:</Text>
          <CurrencyLabel value={invoice} />
          <Text as="b">Pending:</Text>
          <CurrencyLabel value={pendingInvoice} />
          <Text as="b">Commitment:</Text>
          <CurrencyLabel value={commitment} />
        </SimpleGrid>
        <Divider />
        <SimpleGrid columns={2} gap={{ base: 1, md: 2 }}>
          <Text as="b">Commitment Remaining:</Text>
          <CurrencyLabel value={remaining} />
        </SimpleGrid>
      </Stack>
    );
    return (
      <Tooltip hasArrow label={<Notes />}>
        <ColorfulBar
          colorSchemes={['pink', 'yellow']}
          values={[invoice, pendingInvoice]}
          max={commitmentTotal}
          minW={minWidth}
          {...other}
        />
      </Tooltip>
    );
  }
);

export interface CommitmentVsBudgetBarProps {
  commitment: any | undefined;
  pending: any | undefined;
  budget: any | undefined;
  contingency: any | undefined;
}

export const CommitmentVsBudgetBar = chakra(
  (props: CommitmentVsBudgetBarProps) => {
    const {
      commitment = 0,
      pending = 0,
      budget = 0,
      contingency = 0,
      ...other
    } = props;
    const max = useMemo(() => {
      return parseFloat(budget) + parseFloat(contingency);
    }, [budget, contingency]);

    const total = useMemo(
      () => parseFloat(commitment) + parseFloat(pending),
      [commitment, pending]
    );
    const remaining = useMemo(() => Math.max(0, max - total), [total, max]);

    const Notes = () => (
      <Stack p={{ base: 1, md: 2 }}>
        <SimpleGrid columns={2} gap={{ base: 1, md: 2 }}>
          <Text as="b">Commitment:</Text>
          <CurrencyLabel value={commitment} />
          <Text as="b">Pending:</Text>
          <CurrencyLabel value={pending} />
          <Text as="b">Budget:</Text>
          <CurrencyLabel value={budget} />
          <Text as="b">Contingency:</Text>
          <CurrencyLabel value={contingency} />
        </SimpleGrid>
        <Divider />
        <SimpleGrid columns={2} gap={{ base: 1, md: 2 }}>
          <Text as="b">Total:</Text>
          <CurrencyLabel value={total} />
          <Text as="b">Available:</Text>
          <CurrencyLabel value={remaining} />
        </SimpleGrid>
      </Stack>
    );
    return (
      <Tooltip hasArrow label={<Notes />}>
        <ColorfulBar
          colorSchemes={['pink', 'yellow']}
          values={[commitment, pending]}
          max={max}
          minW={minWidth}
          {...other}
        />
      </Tooltip>
    );
  }
);

export interface AfeVsBoardProjectsBarProps {
  budget: any | undefined;
  contingency: any | undefined;
  pendingBudget: any | undefined;
  pendingContingency: any | undefined;
  bpCapacity: any | undefined;
}

export const AfeVsBoardProjectsBar = forwardRef(
  (props: AfeVsBoardProjectsBarProps, ref) => {
    const {
      bpCapacity = 0,
      budget = 0,
      contingency = 0,
      pendingBudget = 0,
      pendingContingency = 0,
      ...other
    } = props;

    const max = useMemo(() => {
      return parseFloat(bpCapacity);
    }, [bpCapacity]);

    const pending = useMemo(() => {
      return parseFloat(pendingBudget) + parseFloat(pendingContingency);
    }, [pendingBudget, pendingContingency]);

    return (
      <ColorfulBar
        max={max}
        ref={ref}
        values={[budget, contingency, pending]}
        colorSchemes={['pink', 'orange', 'yellow']}
        minW={minWidth}
        {...other}
      />
    );
  }
);

export interface AfeVsEacBarProps {
  eac: any | undefined;
  commitment: any | undefined;
  invoiced: any | undefined;
}
export const AfeVsEacBar = forwardRef((props: AfeVsEacBarProps, ref) => {
  const { eac = 0, commitment = 0, invoiced = 0, ...other } = props;

  const data = useMemo(() => {
    const c = parseFloat(commitment);
    const i = parseFloat(invoiced);
    return [i, c - i];
  }, [commitment, invoiced]);
  return (
    <ColorfulBar
      values={data}
      colorSchemes={['pink', 'yellow']}
      ref={ref}
      max={parseFloat(eac)}
      minW={minWidth}
      {...other}
    />
  );
});
